/**
 * Messages between the service worker (src/sw/sw.ts) and the page
 * (src/features/offline). Both sides import this module, so the shapes stay in step.
 */

/** Service worker to page: what is cached. Sent on activation, on request and after tile writes. */
export const SW_STATUS = 'gemdt:sw-status';
/** Page to service worker: ask for a fresh SW_STATUS (the answer goes to every window). */
export const SW_STATUS_REQUEST = 'gemdt:sw-status-request';
/** Page to the waiting service worker: activate now. workbox-window's messageSkipWaiting sends this. */
export const SKIP_WAITING = 'SKIP_WAITING';
/**
 * Page to a service worker, with a MessagePort: is `url` in your precache? The worker answers
 * { has: boolean } on the port. The page asks about its own entry chunk to learn whether that
 * worker serves the build the page is running.
 */
export const SW_HAS_URL = 'gemdt:sw-has-url';

/**
 * Page to the controlling service worker, with a MessagePort: delete the runtime caches that
 * hold signed-in data and map tiles (src/sw/routes.ts LOGOUT_CLEARED_CACHES). The worker
 * answers SwClearRuntimeReply on the port. Sent by the logout (src/features/auth).
 */
export const SW_CLEAR_RUNTIME = 'gemdt:sw-clear-runtime';

export type SwClearRuntimeReply = { ok: true; cleared: string[] } | { ok: false; error: string };

export function isSwClearRuntimeReply(data: unknown): data is SwClearRuntimeReply {
  if (!data || typeof data !== 'object') return false;
  const m = data as Record<string, unknown>;
  if (m.ok === true) return Array.isArray(m.cleared) && m.cleared.every((n) => typeof n === 'string');
  return m.ok === false && typeof m.error === 'string';
}

export interface SwHasUrlRequest {
  type: typeof SW_HAS_URL;
  url: string;
}

export interface SwStatusMessage {
  type: typeof SW_STATUS;
  /** Every precache entry outside /data (shell HTML, hashed bundles, fonts, Cesium runtime, models, images) is cached. */
  shellCached: boolean;
  /** Every bundled data file under /data (loader JSON, feed snapshots, elements, twin assets, timelines, knowledge) is cached. */
  dataCached: boolean;
  /** Entries in the runtime map tile cache. */
  tilesCached: number;
  /** Precache revision of the shell HTML: changes with every build, identifies the worker's version. */
  shellRevision: string | null;
  /** When the service worker computed this status (ms since the epoch). */
  at: number;
}

export function isSwStatusMessage(data: unknown): data is SwStatusMessage {
  if (!data || typeof data !== 'object') return false;
  const m = data as Record<string, unknown>;
  return (
    m.type === SW_STATUS &&
    typeof m.shellCached === 'boolean' &&
    typeof m.dataCached === 'boolean' &&
    typeof m.tilesCached === 'number' &&
    typeof m.at === 'number'
  );
}
